import {useEffect, useMemo, useRef, useState} from 'react';
import {addDoc, arrayUnion, collection, deleteDoc, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, setDoc, updateDoc, writeBatch} from 'firebase/firestore';
import {deleteObject, getDownloadURL, listAll, ref as storageRef, uploadBytes} from 'firebase/storage';
import {signInWithEmailAndPassword, signOut} from 'firebase/auth';
import {auth, db, storage} from '../utils/firebase';
import {useAuth} from '../context/AuthContext';
import {DEFAULT_SUBS_VISIBILITY} from '../hooks/useSubscriptions';
import {DEFAULT_CODE_GEN_ENABLED} from '../utils/codeGen';
import {DEFAULT_PREMIUM_TICKETS_ENABLED} from '../utils/premiumTickets';
import {DEFAULT_HIGH_ODDS_AI_ENABLED} from '../utils/highOddsAI';
import {DEFAULT_TECH_ISSUE_BANNER_ENABLED} from '../utils/techIssueBanner';
import {DEFAULT_OPEN_WEBSITE_BUTTON_ENABLED} from '../utils/openWebsiteButton';
import {compressImage} from '../utils/imageCompression';
import Api from '../utils/Api';

const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    return date.toLocaleString('ro-RO', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const StatusBadge = ({status}) => {
    const isAvailable = status === 'available';
    return (
        <span className={
            'inline-block px-2 py-0.5 rounded-full text-xs font-bold ' +
            (isAvailable ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700')
        }>
            {isAvailable ? 'available' : 'delivered'}
        </span>
    );
};

const ApanelLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [checking, setChecking] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setChecking(true);
        setError('');
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
            setError('Autentificare esuata: verifica email-ul si parola');
        }
        setChecking(false);
    };

    return (
        <div className="min-h-screen bg-[#34495e] flex items-center justify-center p-5">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 w-full max-w-[340px]">
                <h1 className="text-lg font-bold text-center mb-4">Admin Panel</h1>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    autoFocus
                    className="w-full px-3 py-2 border rounded mb-3"
                />
                <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Parola"
                    className="w-full px-3 py-2 border rounded mb-3"
                />
                {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
                <button
                    type="submit"
                    disabled={checking}
                    className="w-full py-2 bg-[#2ecc71] hover:bg-green-600 text-white font-bold rounded transition"
                >
                    {checking ? '...' : 'Login'}
                </button>
            </form>
        </div>
    );
};

const ApanelAccessDenied = () => (
    <div className="min-h-screen bg-[#34495e] flex items-center justify-center p-5">
        <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-[340px] text-center space-y-3">
            <h1 className="text-lg font-bold">Acces interzis</h1>
            <p className="text-sm text-gray-500">Acest cont nu are drepturi de administrator.</p>
            <button
                onClick={() => signOut(auth)}
                className="w-full py-2 bg-gray-500 hover:bg-gray-600 text-white font-bold rounded transition"
            >
                Logout
            </button>
        </div>
    </div>
);

const SUBS_TOGGLES = [
    {key: 'week1', label: 'Abonament 1 saptamana (24.99 lei)'},
    {key: 'sub1', label: 'Abonament 1 luna (59.99 lei)'},
];

const SubsSettings = () => {
    const [visibility, setVisibility] = useState(null);
    const [saving, setSaving] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const snap = await getDoc(doc(db, 'subsSettings', 'visibility'));
                setVisibility({...DEFAULT_SUBS_VISIBILITY, ...(snap.exists() ? snap.data() : {})});
            } catch (err) {
                console.error(err);
                setError('Eroare la incarcarea setarilor: ' + err.message);
            }
        };
        load();
    }, []);

    const toggle = async (key) => {
        const newValue = !visibility[key];
        setSaving(key);
        setError('');
        try {
            await setDoc(doc(db, 'subsSettings', 'visibility'), {[key]: newValue}, {merge: true});
            setVisibility(prev => ({...prev, [key]: newValue}));
        } catch (err) {
            console.error(err);
            setError('Salvarea a esuat: ' + err.message);
        }
        setSaving('');
    };

    return (
        <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <h2 className="font-bold">Vizibilitate abonamente</h2>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            {!visibility && !error && <p className="text-sm text-gray-400">Se incarca...</p>}
            {visibility && SUBS_TOGGLES.map(({key, label}) => (
                <div key={key} className="flex items-center justify-between gap-3">
                    <span className="text-sm">{label}</span>
                    <button
                        onClick={() => toggle(key)}
                        disabled={saving === key}
                        className={
                            'px-3 py-1.5 rounded text-xs font-bold text-white min-w-[90px] transition ' +
                            (visibility[key] ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400 hover:bg-gray-500')
                        }
                    >
                        {saving === key ? '...' : (visibility[key] ? 'AFISAT' : 'ASCUNS')}
                    </button>
                </div>
            ))}
            <p className="text-xs text-gray-400">
                Click pe buton pentru a afisa/ascunde abonamentul pe pagina de achizitie. Restul abonamentelor nu sunt afectate.
            </p>
        </div>
    );
};

const CodeGenSettings = () => {
    const [enabled, setEnabled] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const snap = await getDoc(doc(db, 'appSettings', 'codeGeneration'));
                setEnabled(snap.exists() && typeof snap.data().enabled === 'boolean'
                    ? snap.data().enabled
                    : DEFAULT_CODE_GEN_ENABLED);
            } catch (err) {
                console.error(err);
                setError('Eroare la incarcarea setarilor: ' + err.message);
            }
        };
        load();
    }, []);

    const toggle = async () => {
        const newValue = !enabled;
        setSaving(true);
        setError('');
        try {
            await setDoc(doc(db, 'appSettings', 'codeGeneration'), {enabled: newValue}, {merge: true});
            setEnabled(newValue);
        } catch (err) {
            console.error(err);
            setError('Salvarea a esuat: ' + err.message);
        }
        setSaving(false);
    };

    return (
        <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <h2 className="font-bold">Generare bilete cu cod</h2>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            {enabled === null && !error && <p className="text-sm text-gray-400">Se incarca...</p>}
            {enabled !== null && (
                <div className="flex items-center justify-between gap-3">
                    <span className="text-sm">Buton "Genereaza un bilet"</span>
                    <button
                        onClick={toggle}
                        disabled={saving}
                        className={
                            'px-3 py-1.5 rounded text-xs font-bold text-white min-w-[90px] transition ' +
                            (enabled ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400 hover:bg-gray-500')
                        }
                    >
                        {saving ? '...' : (enabled ? 'ACTIV' : 'BLOCAT')}
                    </button>
                </div>
            )}
            <p className="text-xs text-gray-400">
                Activeaza sau dezactiveaza sectiunea "Generare bilete cu cod", vizibila pentru toate conturile.
            </p>
        </div>
    );
};

const PremiumTicketsSettings = () => {
    const [enabled, setEnabled] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const snap = await getDoc(doc(db, 'appSettings', 'premiumTickets'));
                setEnabled(snap.exists() && typeof snap.data().enabled === 'boolean'
                    ? snap.data().enabled
                    : DEFAULT_PREMIUM_TICKETS_ENABLED);
            } catch (err) {
                console.error(err);
                setError('Eroare la incarcarea setarilor: ' + err.message);
            }
        };
        load();
    }, []);

    const toggle = async () => {
        const newValue = !enabled;
        setSaving(true);
        setError('');
        try {
            await setDoc(doc(db, 'appSettings', 'premiumTickets'), {enabled: newValue}, {merge: true});
            setEnabled(newValue);
        } catch (err) {
            console.error(err);
            setError('Salvarea a esuat: ' + err.message);
        }
        setSaving(false);
    };

    return (
        <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <h2 className="font-bold">Bilete castigatoare premium/cod</h2>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            {enabled === null && !error && <p className="text-sm text-gray-400">Se incarca...</p>}
            {enabled !== null && (
                <div className="flex items-center justify-between gap-3">
                    <span className="text-sm">Sectiune</span>
                    <button
                        onClick={toggle}
                        disabled={saving}
                        className={
                            'px-3 py-1.5 rounded text-xs font-bold text-white min-w-[90px] transition ' +
                            (enabled ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400 hover:bg-gray-500')
                        }
                    >
                        {saving ? '...' : (enabled ? 'ACTIV' : 'DEZACTIVAT')}
                    </button>
                </div>
            )}
            <p className="text-xs text-gray-400">
                Activeaza sau dezactiveaza sectiunea cu imaginile biletelor castigatoare, vizibila pentru toate conturile.
            </p>
        </div>
    );
};

const HighOddsAISettings = () => {
    const [enabled, setEnabled] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const snap = await getDoc(doc(db, 'appSettings', 'highOddsAI'));
                setEnabled(snap.exists() && typeof snap.data().enabled === 'boolean'
                    ? snap.data().enabled
                    : DEFAULT_HIGH_ODDS_AI_ENABLED);
            } catch (err) {
                console.error(err);
                setError('Eroare la incarcarea setarilor: ' + err.message);
            }
        };
        load();
    }, []);

    const toggle = async () => {
        const newValue = !enabled;
        setSaving(true);
        setError('');
        try {
            await setDoc(doc(db, 'appSettings', 'highOddsAI'), {enabled: newValue}, {merge: true});
            setEnabled(newValue);
        } catch (err) {
            console.error(err);
            setError('Salvarea a esuat: ' + err.message);
        }
        setSaving(false);
    };

    return (
        <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <h2 className="font-bold">Pronosticuri Cote Mari</h2>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            {enabled === null && !error && <p className="text-sm text-gray-400">Se incarca...</p>}
            {enabled !== null && (
                <div className="flex items-center justify-between gap-3">
                    <span className="text-sm">Sectiune</span>
                    <button
                        onClick={toggle}
                        disabled={saving}
                        className={
                            'px-3 py-1.5 rounded text-xs font-bold text-white min-w-[90px] transition ' +
                            (enabled ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400 hover:bg-gray-500')
                        }
                    >
                        {saving ? '...' : (enabled ? 'ACTIV' : 'DEZACTIVAT')}
                    </button>
                </div>
            )}
            <p className="text-xs text-gray-400">
                Activeaza sau dezactiveaza sectiunea "Pronosticuri Cote Mari", vizibila pentru toate conturile.
            </p>
        </div>
    );
};

const TechIssueBannerSettings = () => {
    const [enabled, setEnabled] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const snap = await getDoc(doc(db, 'appSettings', 'techIssueBanner'));
                setEnabled(snap.exists() && typeof snap.data().enabled === 'boolean'
                    ? snap.data().enabled
                    : DEFAULT_TECH_ISSUE_BANNER_ENABLED);
            } catch (err) {
                console.error(err);
                setError('Eroare la incarcarea setarilor: ' + err.message);
            }
        };
        load();
    }, []);

    const toggle = async () => {
        const newValue = !enabled;
        setSaving(true);
        setError('');
        try {
            await setDoc(doc(db, 'appSettings', 'techIssueBanner'), {enabled: newValue}, {merge: true});
            setEnabled(newValue);
        } catch (err) {
            console.error(err);
            setError('Salvarea a esuat: ' + err.message);
        }
        setSaving(false);
    };

    return (
        <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <h2 className="font-bold">Banner problema tehnica</h2>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            {enabled === null && !error && <p className="text-sm text-gray-400">Se incarca...</p>}
            {enabled !== null && (
                <div className="flex items-center justify-between gap-3">
                    <span className="text-sm">Banner</span>
                    <button
                        onClick={toggle}
                        disabled={saving}
                        className={
                            'px-3 py-1.5 rounded text-xs font-bold text-white min-w-[90px] transition ' +
                            (enabled ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400 hover:bg-gray-500')
                        }
                    >
                        {saving ? '...' : (enabled ? 'ACTIV' : 'DEZACTIVAT')}
                    </button>
                </div>
            )}
            <p className="text-xs text-gray-400">
                Afiseaza sau ascunde bannerul "Intampinam o problema tehnica..." deasupra caruselului de pe prima pagina.
            </p>
        </div>
    );
};

const OpenWebsiteButtonSettings = () => {
    const [enabled, setEnabled] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const snap = await getDoc(doc(db, 'appSettings', 'openWebsiteButton'));
                setEnabled(snap.exists() && typeof snap.data().enabled === 'boolean'
                    ? snap.data().enabled
                    : DEFAULT_OPEN_WEBSITE_BUTTON_ENABLED);
            } catch (err) {
                console.error(err);
                setError('Eroare la incarcarea setarilor: ' + err.message);
            }
        };
        load();
    }, []);

    const toggle = async () => {
        const newValue = !enabled;
        setSaving(true);
        setError('');
        try {
            await setDoc(doc(db, 'appSettings', 'openWebsiteButton'), {enabled: newValue}, {merge: true});
            setEnabled(newValue);
        } catch (err) {
            console.error(err);
            setError('Salvarea a esuat: ' + err.message);
        }
        setSaving(false);
    };

    return (
        <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <h2 className="font-bold">Buton "Mergi la website"</h2>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            {enabled === null && !error && <p className="text-sm text-gray-400">Se incarca...</p>}
            {enabled !== null && (
                <div className="flex items-center justify-between gap-3">
                    <span className="text-sm">Buton</span>
                    <button
                        onClick={toggle}
                        disabled={saving}
                        className={
                            'px-3 py-1.5 rounded text-xs font-bold text-white min-w-[90px] transition ' +
                            (enabled ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400 hover:bg-gray-500')
                        }
                    >
                        {saving ? '...' : (enabled ? 'ACTIV' : 'DEZACTIVAT')}
                    </button>
                </div>
            )}
            <p className="text-xs text-gray-400">
                Afiseaza sau ascunde butonul "Mergi la website" de pe prima pagina (jos, dupa Termeni si conditii), care duce userul catre latg.ro.
            </p>
        </div>
    );
};

const TicketRow = ({ticket, expanded, onToggle, onDelete}) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async (e) => {
        e.stopPropagation();
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(ticket.code);
            } else {
                // fallback pentru browsere vechi / context nesecurizat
                const textarea = document.createElement('textarea');
                textarea.value = ticket.code;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            alert('Copierea a esuat: ' + err.message);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow">
            <div
                className="grid grid-cols-2 md:grid-cols-7 gap-2 items-center p-3 cursor-pointer hover:bg-gray-50"
                onClick={onToggle}
            >
                <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-sm">{ticket.code}</span>
                    <button
                        onClick={handleCopy}
                        className={
                            'px-1.5 py-0.5 text-[10px] font-bold rounded whitespace-nowrap ' +
                            (copied ? 'bg-green-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700')
                        }
                    >
                        {copied ? 'Copiat!' : 'Copiaza'}
                    </button>
                </div>
                <div className="text-sm text-right md:text-left">
                    <span className="md:hidden text-gray-400">meciuri: </span>{ticket.matchCount}
                </div>
                <div className="text-sm">
                    <span className="md:hidden text-gray-400">cota: </span>
                    <span className="font-bold">{ticket.totalOdd}</span>
                </div>
                <div className="text-sm text-right md:text-left">
                    <span className="md:hidden text-gray-400">categorie: </span>{ticket.oddCategory}
                </div>
                <div className="text-sm">{ticket.period}</div>
                <div className="text-right md:text-left">
                    <StatusBadge status={ticket.status}/>
                </div>
                <div className="col-span-2 md:col-span-1 flex items-center justify-between md:justify-end gap-2">
                    <span className="text-xs text-gray-500">{formatDate(ticket.createdAt)}</span>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(ticket);
                        }}
                        className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded"
                    >
                        Sterge
                    </button>
                </div>
            </div>

            {expanded && (
                <div className="border-t px-3 py-2 bg-gray-50 rounded-b-lg">
                    {ticket.deliveredAt && (
                        <p className="text-xs text-gray-500 mb-2">Livrat: {formatDate(ticket.deliveredAt)}</p>
                    )}
                    {(ticket.matches || []).map((match, idx) => (
                        <div key={idx} className="flex flex-wrap items-center justify-between gap-x-3 py-1 border-b last:border-b-0 text-sm">
                            <span className="font-semibold">{match.team1} vs {match.team2}</span>
                            <span className="text-gray-600">{match.marketName} → {match.market}</span>
                            <span className="font-bold text-green-600">{match.odd}</span>
                            <span className="text-xs text-gray-500">{match.day} {match.hour}</span>
                        </div>
                    ))}
                    {(!ticket.matches || ticket.matches.length === 0) && (
                        <p className="text-sm text-gray-400">Fara meciuri pe acest bilet.</p>
                    )}
                </div>
            )}
        </div>
    );
};

const IMAGES_FOLDER = 'ticketImages';

const ImagesTab = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState('');
    const [uploadError, setUploadError] = useState('');

    const loadImages = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const folderRef = storageRef(storage, IMAGES_FOLDER);
            const res = await listAll(folderRef);
            const items = await Promise.all(res.items.map(async (item) => ({
                name: item.name,
                fullPath: item.fullPath,
                url: await getDownloadURL(item),
            })));
            items.sort((a, b) => b.name.localeCompare(a.name));
            setImages(items);
        } catch (err) {
            console.error(err);
            setLoadError('Eroare la incarcarea imaginilor: ' + err.message);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadImages();
    }, []);

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (files.length === 0) return;

        setUploading(true);
        setUploadError('');
        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                setUploadProgress(`Se incarca ${i + 1}/${files.length}...`);
                const compressed = await compressImage(file);
                const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
                const path = `${IMAGES_FOLDER}/${Date.now()}_${i}_${safeName.replace(/\.[^.]+$/, '')}.jpg`;
                await uploadBytes(storageRef(storage, path), compressed, {contentType: 'image/jpeg'});
            }
            await loadImages();
        } catch (err) {
            console.error(err);
            setUploadError('Incarcarea a esuat: ' + err.message);
        }
        setUploadProgress('');
        setUploading(false);
    };

    const handleDelete = async (image) => {
        if (!window.confirm('Sigur stergi aceasta imagine?')) return;
        try {
            await deleteObject(storageRef(storage, image.fullPath));
            setImages(prev => prev.filter(i => i.fullPath !== image.fullPath));
        } catch (err) {
            console.error(err);
            alert('Stergerea a esuat: ' + err.message);
        }
    };

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-lg shadow p-4 space-y-3">
                <h2 className="font-bold">Incarca imagini (bilete)</h2>
                <input
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={uploading}
                    onChange={handleUpload}
                    className="text-sm"
                />
                {uploading && <p className="text-sm text-gray-500">{uploadProgress}</p>}
                {uploadError && <p className="text-red-500 text-sm">{uploadError}</p>}
                <p className="text-xs text-gray-400">
                    Imaginile sunt comprimate automat (max 1600px, JPEG) inainte de a fi urcate.
                </p>
            </div>

            <div className="flex items-center justify-between">
                <h2 className="text-white font-bold">Imagini incarcate ({images.length})</h2>
                <button
                    onClick={loadImages}
                    disabled={loading}
                    className="px-3 py-1.5 bg-[#2ecc71] hover:bg-green-600 text-white text-sm font-bold rounded"
                >
                    Reincarca
                </button>
            </div>

            {loading && <p className="text-gray-300 text-center py-8">Se incarca...</p>}
            {loadError && <p className="text-red-300 text-center py-8">{loadError}</p>}
            {!loading && !loadError && images.length === 0 && (
                <p className="text-gray-300 text-center py-8">Nicio imagine incarcata.</p>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {images.map(image => (
                    <div key={image.fullPath} className="bg-white rounded-lg shadow overflow-hidden">
                        <a href={image.url} target="_blank" rel="noopener noreferrer">
                            <img src={image.url} alt={image.name} className="w-full h-32 object-cover"/>
                        </a>
                        <div className="p-2 flex items-center justify-between gap-2">
                            <button
                                onClick={() => navigator.clipboard.writeText(image.url)}
                                className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                            >
                                Copiaza link
                            </button>
                            <button
                                onClick={() => handleDelete(image)}
                                className="text-xs px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded"
                            >
                                Sterge
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const REPLY_AUTHOR = 'LATG';

const ReviewCardAdmin = ({review, onApprove, onReject, onSaveReply}) => {
    const [replyText, setReplyText] = useState(review.reply?.message || '');
    const [saving, setSaving] = useState(false);

    const handleSaveReply = async () => {
        setSaving(true);
        await onSaveReply(review, replyText.trim());
        setSaving(false);
    };

    return (
        <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <p className="font-bold text-sm">{review.name}</p>
                        <span className="text-yellow-500 text-sm">{'★'.repeat(Math.round(review.stars))}{'☆'.repeat(5 - Math.round(review.stars))}</span>
                    </div>
                    <p className="text-xs text-gray-400">{review.dateMesaj} · {review.language}</p>
                </div>
                <span className={
                    'text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ' +
                    (review.status ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700')
                }>
                    {review.status ? 'Aprobat' : 'In asteptare'}
                </span>
            </div>

            <p className="text-sm text-gray-700">{review.message}</p>

            <div className="flex gap-2">
                {!review.status ? (
                    <button
                        onClick={() => onApprove(review)}
                        className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-bold rounded"
                    >
                        Aproba
                    </button>
                ) : (
                    <button
                        onClick={() => onReject(review)}
                        className="px-3 py-1.5 bg-gray-400 hover:bg-gray-500 text-white text-xs font-bold rounded"
                    >
                        Respinge
                    </button>
                )}
            </div>

            <div className="border-t pt-3 space-y-2">
                <p className="text-xs font-bold text-gray-500">Raspuns ({REPLY_AUTHOR})</p>
                <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Scrie un raspuns..."
                    className="w-full px-3 py-2 border rounded text-sm h-20"
                />
                <button
                    onClick={handleSaveReply}
                    disabled={saving}
                    className="px-3 py-1.5 bg-[#8e44ad] hover:bg-purple-700 text-white text-xs font-bold rounded"
                >
                    {saving ? '...' : (review.reply ? 'Actualizeaza raspunsul' : 'Trimite raspuns')}
                </button>
            </div>
        </div>
    );
};

const ReviewsTab = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const loadReviews = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const snap = await getDocs(collection(db, 'reviews'));
            const list = [];
            snap.forEach(d => {
                const data = d.data();
                list.push({
                    id: d.id,
                    name: data.nume || 'anonymous',
                    message: data.mesajReview || '',
                    stars: parseFloat(data.numarStele) || 0,
                    dateMesaj: data.dataMesaj || '',
                    timeStamp: data.timeStamp || 0,
                    language: data.language || '',
                    status: data.status === true,
                    reply: data.reply || null,
                });
            });
            list.sort((a, b) => (b.timeStamp || 0) - (a.timeStamp || 0));
            setReviews(list);
        } catch (err) {
            console.error(err);
            setLoadError('Eroare la incarcarea review-urilor: ' + err.message);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadReviews();
    }, []);

    const filteredReviews = useMemo(() => reviews.filter(r => {
        if (statusFilter === 'approved') return r.status === true;
        if (statusFilter === 'pending') return r.status !== true;
        return true;
    }), [reviews, statusFilter]);

    const setStatus = async (review, status) => {
        try {
            await updateDoc(doc(db, 'reviews', review.id), {status});
            setReviews(prev => prev.map(r => r.id === review.id ? {...r, status} : r));
        } catch (err) {
            console.error(err);
            alert('Salvarea a esuat: ' + err.message);
        }
    };

    const saveReply = async (review, message) => {
        try {
            const reply = message ? {message, authorName: REPLY_AUTHOR, timeStamp: Date.now()} : null;
            await updateDoc(doc(db, 'reviews', review.id), {reply});
            setReviews(prev => prev.map(r => r.id === review.id ? {...r, reply} : r));
        } catch (err) {
            console.error(err);
            alert('Salvarea raspunsului a esuat: ' + err.message);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-white font-bold">Reviews ({filteredReviews.length})</h2>
                <button
                    onClick={loadReviews}
                    disabled={loading}
                    className="px-3 py-1.5 bg-[#2ecc71] hover:bg-green-600 text-white text-sm font-bold rounded"
                >
                    Reincarca
                </button>
            </div>

            <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-2 py-1.5 rounded text-sm"
            >
                <option value="">Status: toate</option>
                <option value="pending">In asteptare</option>
                <option value="approved">Aprobate</option>
            </select>

            {loading && <p className="text-gray-300 text-center py-8">Se incarca...</p>}
            {loadError && <p className="text-red-300 text-center py-8">{loadError}</p>}
            {!loading && !loadError && filteredReviews.length === 0 && (
                <p className="text-gray-300 text-center py-8">Niciun review.</p>
            )}

            <div className="space-y-3">
                {filteredReviews.map(review => (
                    <ReviewCardAdmin
                        key={review.id}
                        review={review}
                        onApprove={(r) => setStatus(r, true)}
                        onReject={(r) => setStatus(r, false)}
                        onSaveReply={saveReply}
                    />
                ))}
            </div>
        </div>
    );
};

// siguranta impotriva unei bucle care nu se mai opreste daca Stripe returneaza mereu has_more
// (pagina Stripe are 20 clienti acum, ca sa nu lovim rate limit-ul pe intervale mari)
const MAX_PAGES_PER_WINDOW = 200;
const PAGE_DELAY_MS = 250;
const MAX_RANDOM_ATTEMPTS = 25;
const RANDOM_LOOKBACK_DAYS = 3 * 365;
const RANDOM_WINDOW_START_DAYS = 1;

const dateToUnix = (dateStr, endOfDay) => {
    const iso = `${dateStr}T${endOfDay ? '23:59:59' : '00:00:00'}Z`;
    return Math.floor(new Date(iso).getTime() / 1000);
};

const formatCustomerCreated = (unixSeconds) => {
    if (!unixSeconds) return '-';
    return new Date(unixSeconds * 1000).toLocaleDateString('ro-RO', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
};

// fereastra aleatorie de timp in ultimii RANDOM_LOOKBACK_DAYS zile, cu marimea data
const pickRandomWindow = (windowDays) => {
    const now = Math.floor(Date.now() / 1000);
    const lookbackSeconds = RANDOM_LOOKBACK_DAYS * 24 * 3600;
    const windowSeconds = windowDays * 24 * 3600;
    const earliestStart = now - lookbackSeconds;
    const latestStart = now - windowSeconds;
    const start = earliestStart + Math.floor(Math.random() * Math.max(1, latestStart - earliestStart));
    return {gte: start, lte: Math.min(start + windowSeconds, now)};
};

const CustomerDeleteRow = ({customer, onDeleted, selected, onToggleSelect, busy}) => {
    const [deleting, setDeleting] = useState(false);

    const handleDelete = async () => {
        const confirmed = window.confirm(
            `Stergi definitiv contul ${customer.email || customer.id}?\n\n` +
            `Se sterge clientul din Stripe, contul de autentificare Firebase si documentul din baza de date.\n` +
            `Aceasta actiune NU poate fi anulata.`
        );
        if (!confirmed) return;

        setDeleting(true);
        try {
            const result = await Api.deleteCustomerAccount(customer.email, customer.id);
            if (result.warnings?.length) {
                alert('Sters partial - avertismente:\n' + result.warnings.join('\n'));
            }
            onDeleted(customer.id);
        } catch (err) {
            console.error(err);
            const detail = err?.error || err?.message || (typeof err === 'string' ? err : JSON.stringify(err));
            alert('Stergerea a esuat: ' + detail);
        }
        setDeleting(false);
    };

    return (
        <tr className="border-b last:border-0">
            <td className="p-3">
                <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggleSelect(customer.id)}
                    disabled={busy || deleting}
                />
            </td>
            <td className="p-3">{customer.email || '-'}</td>
            <td className="p-3">{customer.name || '-'}</td>
            <td className="p-3">{formatCustomerCreated(customer.created)}</td>
            <td className="p-3 font-mono text-xs">{customer.id}</td>
            <td className="p-3 text-right">
                <button
                    onClick={handleDelete}
                    disabled={busy || deleting}
                    className="px-2 py-1 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-xs font-bold rounded"
                >
                    {deleting ? '...' : 'Sterge cont'}
                </button>
            </td>
        </tr>
    );
};

const CustomersWithoutSubscriptionTab = () => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [targetCount, setTargetCount] = useState(50);
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState([]);
    const [pagesScanned, setPagesScanned] = useState(0);
    const [customersScanned, setCustomersScanned] = useState(0);
    const [attemptInfo, setAttemptInfo] = useState('');
    const [error, setError] = useState('');
    const stopRef = useRef(false);

    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [bulkProgress, setBulkProgress] = useState({done: 0, total: 0});
    const bulkStopRef = useRef(false);

    // scaneaza un interval [gte, lte], adaugand rezultate noi in `found` (dedup dupa id),
    // opreste cand atinge target sau epuizeaza intervalul; returneaza true daca a atins target
    const scanWindow = async (gte, lte, found, target) => {
        let cursor = undefined;
        let hasMore = true;
        let page = 0;

        while (hasMore && page < MAX_PAGES_PER_WINDOW && !stopRef.current && found.size < target) {
            const data = await Api.customersWithoutSubscription(gte, lte, cursor);
            page += 1;
            setPagesScanned(prev => prev + 1);
            setCustomersScanned(prev => prev + (data.scannedInPage || 0));
            (data.customers || []).forEach(c => found.set(c.id, c));
            setResults(Array.from(found.values()));
            hasMore = data.hasMore;
            cursor = data.nextCursor;
            if (hasMore && found.size < target) {
                await new Promise(resolve => setTimeout(resolve, PAGE_DELAY_MS));
            }
        }
        return found.size >= target;
    };

    const handleSearch = async () => {
        const target = parseInt(targetCount, 10) || 50;
        stopRef.current = false;
        setSearching(true);
        setError('');
        setResults([]);
        setPagesScanned(0);
        setCustomersScanned(0);
        setAttemptInfo('');
        setSelectedIds(new Set());

        const found = new Map();

        try {
            if (startDate && endDate) {
                setAttemptInfo(`Cautare in intervalul ${startDate} - ${endDate}...`);
                await scanWindow(dateToUnix(startDate, false), dateToUnix(endDate, true), found, target);
            } else {
                // fara interval ales: incercam ferestre aleatorii de timp, marind fereastra
                // treptat daca nu gasim destui, pana atingem target sau epuizam incercarile
                let windowDays = RANDOM_WINDOW_START_DAYS;
                for (let attempt = 1; attempt <= MAX_RANDOM_ATTEMPTS && !stopRef.current && found.size < target; attempt++) {
                    const {gte, lte} = pickRandomWindow(windowDays);
                    setAttemptInfo(
                        `Incercarea ${attempt}/${MAX_RANDOM_ATTEMPTS}: ${new Date(gte * 1000).toLocaleDateString('ro-RO')} - ${new Date(lte * 1000).toLocaleDateString('ro-RO')}`
                    );
                    await scanWindow(gte, lte, found, target);
                    windowDays = Math.min(windowDays * 2, RANDOM_LOOKBACK_DAYS);
                }
            }
        } catch (err) {
            console.error(err);
            setError('Cautarea a esuat: ' + (err?.error || err?.message || 'eroare necunoscuta'));
        }
        setSearching(false);
    };

    const handleStop = () => {
        stopRef.current = true;
    };

    const handleRowDeleted = (id) => {
        setResults(prev => prev.filter(r => r.id !== id));
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        setSelectedIds(prev => (
            prev.size === results.length ? new Set() : new Set(results.map(r => r.id))
        ));
    };

    const handleBulkStop = () => {
        bulkStopRef.current = true;
    };

    // sterge, pe rand (coada), fiecare cont selectat - nu simultan, ca sa nu lovim
    // rate limit-ul Stripe/Firebase la fel ca la cautare
    const handleBulkDelete = async () => {
        const toDelete = results.filter(r => selectedIds.has(r.id));
        if (toDelete.length === 0) return;

        const confirmed = window.confirm(
            `Stergi definitiv ${toDelete.length} conturi selectate?\n\n` +
            `Pentru fiecare: clientul din Stripe, contul de autentificare Firebase si documentul din baza de date.\n` +
            `Aceasta actiune NU poate fi anulata.`
        );
        if (!confirmed) return;

        bulkStopRef.current = false;
        setBulkDeleting(true);
        setBulkProgress({done: 0, total: toDelete.length});

        const failures = [];

        for (let i = 0; i < toDelete.length; i++) {
            if (bulkStopRef.current) break;
            const customer = toDelete[i];
            try {
                const result = await Api.deleteCustomerAccount(customer.email, customer.id);
                if (result.warnings?.length) {
                    failures.push(`${customer.email || customer.id}: ${result.warnings.join('; ')}`);
                }
                handleRowDeleted(customer.id);
            } catch (err) {
                const detail = err?.error || err?.message || (typeof err === 'string' ? err : JSON.stringify(err));
                failures.push(`${customer.email || customer.id}: ${detail}`);
            }
            setBulkProgress({done: i + 1, total: toDelete.length});
            if (i < toDelete.length - 1) {
                await new Promise(resolve => setTimeout(resolve, PAGE_DELAY_MS));
            }
        }

        setBulkDeleting(false);
        if (failures.length > 0) {
            alert(`Gata, dar cu ${failures.length} probleme:\n\n` + failures.join('\n'));
        }
    };

    const exportCsv = () => {
        const header = 'email,nume,customerId,data_creare\n';
        const rows = results.map(c => {
            const created = c.created ? new Date(c.created * 1000).toISOString() : '';
            return [c.email || '', c.name || '', c.id, created].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
        });
        const csv = header + rows.join('\n');
        const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `clienti-fara-abonament-${startDate || 'random'}_${endDate || Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-lg shadow p-4 space-y-3">
                <h2 className="font-bold">Clienti Stripe fara abonament activ</h2>
                <p className="text-xs text-gray-400">
                    Cauta clienti Stripe fara abonament activ/trialing/restant, oprindu-se automat cand gaseste
                    numarul dorit. Daca nu alegi un interval de date, incearca ferestre de timp alese aleatoriu
                    (marite treptat daca nu gaseste destui).
                </p>

                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">De la (optional)</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="px-2 py-1.5 border rounded text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">Pana la (optional)</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="px-2 py-1.5 border rounded text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">Numar dorit</label>
                        <input
                            type="number"
                            min="1"
                            max="500"
                            value={targetCount}
                            onChange={(e) => setTargetCount(e.target.value)}
                            className="px-2 py-1.5 border rounded text-sm w-24"
                        />
                    </div>
                    <button
                        onClick={handleSearch}
                        disabled={searching}
                        className="px-4 py-1.5 bg-[#8e44ad] hover:bg-purple-700 disabled:opacity-60 text-white text-sm font-bold rounded"
                    >
                        {searching ? 'Se cauta...' : 'Cauta'}
                    </button>
                    {searching && (
                        <button
                            onClick={handleStop}
                            className="px-4 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-bold rounded"
                        >
                            Opreste
                        </button>
                    )}
                    {!searching && results.length > 0 && (
                        <button
                            onClick={exportCsv}
                            className="px-4 py-1.5 bg-[#2ecc71] hover:bg-green-600 text-white text-sm font-bold rounded"
                        >
                            Exporta CSV
                        </button>
                    )}
                </div>

                {error && <p className="text-red-500 text-sm">{error}</p>}

                {attemptInfo && <p className="text-xs text-gray-500">{attemptInfo}</p>}

                {(searching || pagesScanned > 0) && (
                    <p className="text-xs text-gray-400">
                        Pagini verificate: {pagesScanned} · Clienti scanati: {customersScanned} · Gasiti fara abonament: {results.length}
                    </p>
                )}
            </div>

            {results.length > 0 && (
                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <div className="flex flex-wrap items-center gap-3 p-3 border-b">
                        <span className="text-sm font-semibold">
                            {selectedIds.size} selectate din {results.length}
                        </span>
                        <button
                            onClick={handleBulkDelete}
                            disabled={selectedIds.size === 0 || bulkDeleting}
                            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold rounded"
                        >
                            {bulkDeleting
                                ? `Se sterg... ${bulkProgress.done}/${bulkProgress.total}`
                                : `Sterge selectate (${selectedIds.size})`}
                        </button>
                        {bulkDeleting && (
                            <button
                                onClick={handleBulkStop}
                                className="px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-xs font-bold rounded"
                            >
                                Opreste coada
                            </button>
                        )}
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                        <tr className="text-left text-gray-500 text-xs uppercase border-b">
                            <th className="p-3">
                                <input
                                    type="checkbox"
                                    checked={results.length > 0 && selectedIds.size === results.length}
                                    onChange={toggleSelectAll}
                                    disabled={bulkDeleting}
                                />
                            </th>
                            <th className="p-3">Email</th>
                            <th className="p-3">Nume</th>
                            <th className="p-3">Creat la</th>
                            <th className="p-3">Customer ID</th>
                            <th className="p-3"></th>
                        </tr>
                        </thead>
                        <tbody>
                        {results.map(c => (
                            <CustomerDeleteRow
                                key={c.id}
                                customer={c}
                                onDeleted={handleRowDeleted}
                                selected={selectedIds.has(c.id)}
                                onToggleSelect={toggleSelect}
                                busy={bulkDeleting}
                            />
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const formatTrackingDate = (timestamp) => {
    if (!timestamp?.toDate) return '-';
    return timestamp.toDate().toLocaleString('ro-RO', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

const TrackingErrorRow = ({event, onDelete}) => {
    const [copied, setCopied] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleCopyEmail = async (e) => {
        e.stopPropagation();
        if (!event.email) return;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(event.email);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = event.email;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            alert('Copierea a esuat: ' + err.message);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Stergi acest eveniment de tracking?')) return;
        setDeleting(true);
        await onDelete(event);
        setDeleting(false);
    };

    return (
        <tr className="border-b last:border-0">
            <td className="p-3 whitespace-nowrap">{formatTrackingDate(event.timestamp)}</td>
            <td className="p-3">
                {event.email ? (
                    <div className="flex items-center gap-2">
                        <span>{event.email}</span>
                        <button
                            onClick={handleCopyEmail}
                            className={
                                'px-1.5 py-0.5 text-[10px] font-bold rounded whitespace-nowrap ' +
                                (copied ? 'bg-green-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700')
                            }
                        >
                            {copied ? 'Copiat!' : 'Copiaza'}
                        </button>
                    </div>
                ) : '-'}
            </td>
            <td className="p-3 font-mono text-xs">{event.errorCode}</td>
            <td className="p-3">{event.errorMessage}</td>
            <td className="p-3 text-right">
                <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="px-2 py-1 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-xs font-bold rounded"
                >
                    {deleting ? '...' : 'Sterge'}
                </button>
            </td>
        </tr>
    );
};

const TrackingTab = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');

    const loadEvents = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const q = query(collection(db, 'trackingUsers'), orderBy('timestamp', 'desc'), limit(500));
            const snap = await getDocs(q);
            const list = [];
            snap.forEach(d => list.push({id: d.id, ...d.data()}));
            setEvents(list);
        } catch (err) {
            console.error(err);
            setLoadError('Eroare la incarcarea datelor: ' + err.message);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadEvents();
    }, []);

    const buttonClicks = useMemo(() => events.filter(e => e.type === 'register_button_click'), [events]);
    const submitErrors = useMemo(() => events.filter(e => e.type === 'register_submit_error'), [events]);

    const handleDeleteEvent = async (event) => {
        try {
            await deleteDoc(doc(db, 'trackingUsers', event.id));
            setEvents(prev => prev.filter(e => e.id !== event.id));
        } catch (err) {
            console.error(err);
            alert('Stergerea a esuat: ' + err.message);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-white font-bold">Tracking Register</h2>
                <button
                    onClick={loadEvents}
                    disabled={loading}
                    className="px-3 py-1.5 bg-[#2ecc71] hover:bg-green-600 text-white text-sm font-bold rounded"
                >
                    Reincarca
                </button>
            </div>

            <p className="text-xs text-gray-300">
                Ultimele {events.length} evenimente (limita 500). Pentru istoric complet, verifica direct in
                Firestore, colectia trackingUsers.
            </p>

            {loading && <p className="text-gray-300 text-center py-8">Se incarca...</p>}
            {loadError && <p className="text-red-300 text-center py-8">{loadError}</p>}

            {!loading && !loadError && (
                <>
                    <div className="flex flex-wrap gap-2 text-sm">
                        <span className="bg-white rounded px-3 py-1.5 font-bold">
                            Click-uri buton Register: {buttonClicks.length}
                        </span>
                        <span className="bg-red-100 text-red-700 rounded px-3 py-1.5 font-bold">
                            Erori la inregistrare: {submitErrors.length}
                        </span>
                    </div>

                    <div className="bg-white rounded-lg shadow overflow-x-auto">
                        <h3 className="font-bold p-3 border-b">Erori la submit (Create Account)</h3>
                        {submitErrors.length === 0 ? (
                            <p className="text-gray-400 text-sm p-3">Nicio eroare inregistrata.</p>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                <tr className="text-left text-gray-500 text-xs uppercase border-b">
                                    <th className="p-3">Data</th>
                                    <th className="p-3">Email</th>
                                    <th className="p-3">Cod eroare</th>
                                    <th className="p-3">Mesaj</th>
                                    <th className="p-3"></th>
                                </tr>
                                </thead>
                                <tbody>
                                {submitErrors.map(ev => (
                                    <TrackingErrorRow key={ev.id} event={ev} onDelete={handleDeleteEvent}/>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

const pad2 = (n) => String(n).padStart(2, '0');

const todayKey = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const formatTaskDate = (key) => (key ? key.split('-').reverse().join('.') : '');

const formatNoteTime = (ms) => {
    if (!ms) return '';
    const d = new Date(ms);
    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const TaskCard = ({task, onToggle, onAddNote, onDelete}) => {
    const [noteText, setNoteText] = useState('');
    const [busy, setBusy] = useState(false);

    const submitNote = async () => {
        const text = noteText.trim();
        if (!text || busy) return;
        setBusy(true);
        const ok = await onAddNote(task, text);
        if (ok) setNoteText('');
        setBusy(false);
    };

    return (
        <div className={'bg-white rounded-lg shadow p-4 space-y-3 ' + (task.done ? 'opacity-70' : '')}>
            <div className="flex items-start gap-3">
                <input
                    type="checkbox"
                    checked={!!task.done}
                    onChange={() => onToggle(task)}
                    className="mt-1 w-5 h-5 cursor-pointer"
                    title={task.done ? 'Marcheaza ca nefacut' : 'Marcheaza ca facut'}
                />
                <div className="flex-1 min-w-0">
                    <p className={'text-sm whitespace-pre-wrap break-words ' + (task.done ? 'line-through text-gray-500' : 'font-semibold')}>
                        {task.text}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                        Adaugat: {formatTaskDate(task.date)}
                        {task.done && task.doneAt ? ` • Facut: ${formatNoteTime(task.doneAt)}` : ''}
                    </p>
                </div>
                <button
                    onClick={() => onDelete(task)}
                    className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-xs font-bold rounded"
                >
                    Sterge
                </button>
            </div>

            {(task.notes || []).length > 0 && (
                <div className="space-y-1 pl-8">
                    {task.notes.map((note, idx) => (
                        <div key={idx} className="bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                            <p className="text-xs whitespace-pre-wrap break-words">{note.text}</p>
                            <p className="text-[10px] text-gray-400">{formatNoteTime(note.at)}</p>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex gap-2 pl-8">
                <input
                    type="text"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') submitNote(); }}
                    placeholder="Adauga o notita..."
                    className="flex-1 border rounded px-2 py-1 text-sm"
                />
                <button
                    onClick={submitNote}
                    disabled={busy || !noteText.trim()}
                    className="px-3 py-1 bg-[#2ecc71] hover:bg-green-600 disabled:bg-gray-300 text-white text-xs font-bold rounded"
                >
                    Adauga
                </button>
            </div>
        </div>
    );
};

const TasksTab = () => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [newText, setNewText] = useState('');
    const [saving, setSaving] = useState(false);
    const [showDone, setShowDone] = useState(false);

    const loadTasks = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const q = query(collection(db, 'adminTasks'), orderBy('createdAt', 'desc'), limit(500));
            const snap = await getDocs(q);
            const list = [];
            snap.forEach(d => list.push({id: d.id, ...d.data()}));
            setTasks(list);
        } catch (err) {
            console.error(err);
            setLoadError('Eroare la incarcarea task-urilor: ' + err.message);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadTasks();
    }, []);

    const handleAdd = async () => {
        const text = newText.trim();
        if (!text || saving) return;
        setSaving(true);
        try {
            const data = {text, date: todayKey(), done: false, doneAt: null, notes: []};
            const ref = await addDoc(collection(db, 'adminTasks'), {...data, createdAt: serverTimestamp()});
            setTasks(prev => [{id: ref.id, ...data}, ...prev]);
            setNewText('');
        } catch (err) {
            console.error(err);
            alert('Adaugarea a esuat: ' + err.message);
        }
        setSaving(false);
    };

    const handleToggle = async (task) => {
        const done = !task.done;
        const doneAt = done ? Date.now() : null;
        try {
            await updateDoc(doc(db, 'adminTasks', task.id), {done, doneAt});
            setTasks(prev => prev.map(tk => tk.id === task.id ? {...tk, done, doneAt} : tk));
        } catch (err) {
            console.error(err);
            alert('Salvarea a esuat: ' + err.message);
        }
    };

    const handleAddNote = async (task, text) => {
        const note = {text, at: Date.now()};
        try {
            await updateDoc(doc(db, 'adminTasks', task.id), {notes: arrayUnion(note)});
            setTasks(prev => prev.map(tk => tk.id === task.id ? {...tk, notes: [...(tk.notes || []), note]} : tk));
            return true;
        } catch (err) {
            console.error(err);
            alert('Salvarea notitei a esuat: ' + err.message);
            return false;
        }
    };

    const handleDelete = async (task) => {
        if (!window.confirm('Stergi acest task?')) return;
        try {
            await deleteDoc(doc(db, 'adminTasks', task.id));
            setTasks(prev => prev.filter(tk => tk.id !== task.id));
        } catch (err) {
            console.error(err);
            alert('Stergerea a esuat: ' + err.message);
        }
    };

    const openTasks = tasks.filter(tk => !tk.done);
    const doneTasks = tasks.filter(tk => tk.done);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-white font-bold">Task-uri</h2>
                <button
                    onClick={loadTasks}
                    disabled={loading}
                    className="px-3 py-1.5 bg-[#2ecc71] hover:bg-green-600 text-white text-sm font-bold rounded"
                >
                    Reincarca
                </button>
            </div>

            <div className="bg-white rounded-lg shadow p-4 space-y-2">
                <textarea
                    value={newText}
                    onChange={(e) => setNewText(e.target.value)}
                    placeholder="Task nou..."
                    rows={2}
                    className="w-full border rounded px-2 py-1 text-sm"
                />
                <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-gray-400">Data: {formatTaskDate(todayKey())}</span>
                    <button
                        onClick={handleAdd}
                        disabled={saving || !newText.trim()}
                        className="px-4 py-1.5 bg-[#2ecc71] hover:bg-green-600 disabled:bg-gray-300 text-white text-sm font-bold rounded"
                    >
                        {saving ? '...' : 'Adauga task'}
                    </button>
                </div>
            </div>

            {loading && <p className="text-gray-300 text-center py-4">Se incarca...</p>}
            {loadError && <p className="text-red-300 text-center py-4">{loadError}</p>}

            {!loading && !loadError && (
                <>
                    <div className="flex flex-wrap gap-2 text-sm">
                        <span className="bg-white rounded px-3 py-1.5 font-bold">De facut: {openTasks.length}</span>
                        <span className="bg-green-100 text-green-700 rounded px-3 py-1.5 font-bold">Facute: {doneTasks.length}</span>
                    </div>

                    {openTasks.length === 0 && (
                        <p className="text-gray-300 text-center text-sm py-4">Niciun task de facut.</p>
                    )}
                    {openTasks.map(task => (
                        <TaskCard key={task.id} task={task} onToggle={handleToggle} onAddNote={handleAddNote} onDelete={handleDelete}/>
                    ))}

                    {doneTasks.length > 0 && (
                        <>
                            <button
                                onClick={() => setShowDone(prev => !prev)}
                                className="text-white text-sm font-bold underline"
                            >
                                {showDone ? 'Ascunde task-urile facute' : `Arata task-urile facute (${doneTasks.length})`}
                            </button>
                            {showDone && doneTasks.map(task => (
                                <TaskCard key={task.id} task={task} onToggle={handleToggle} onAddNote={handleAddNote} onDelete={handleDelete}/>
                            ))}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

const ApanelPage = () => {
    const {currentUser} = useAuth();
    const [isAdmin, setIsAdmin] = useState(null);
    const [activeTab, setActiveTab] = useState('tickets');
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [expandedId, setExpandedId] = useState(null);

    const [statusFilter, setStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [periodFilter, setPeriodFilter] = useState('');

    const [showDeleteAll, setShowDeleteAll] = useState(false);
    const [deleteAllText, setDeleteAllText] = useState('');
    const [working, setWorking] = useState(false);

    const loadTickets = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const snap = await getDocs(collection(db, 'tickets'));
            const list = [];
            snap.forEach(d => list.push({id: d.id, ...d.data()}));
            list.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
            setTickets(list);
        } catch (err) {
            console.error(err);
            setLoadError('Eroare la incarcarea biletelor: ' + err.message);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (!currentUser) {
            setIsAdmin(null);
            return;
        }
        const checkAdmin = async () => {
            try {
                const snap = await getDoc(doc(db, 'admins', currentUser.uid));
                setIsAdmin(snap.exists());
            } catch (err) {
                console.error(err);
                setIsAdmin(false);
            }
        };
        checkAdmin();
    }, [currentUser]);

    useEffect(() => {
        if (isAdmin) {
            loadTickets();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const categories = useMemo(
        () => Array.from(new Set(tickets.map(t => t.oddCategory).filter(c => c !== undefined && c !== null)))
            .sort((a, b) => a - b),
        [tickets]
    );
    const periods = useMemo(
        () => Array.from(new Set(tickets.map(t => t.period).filter(Boolean))),
        [tickets]
    );

    const filteredTickets = useMemo(() => tickets.filter(t => {
        if (statusFilter && t.status !== statusFilter) return false;
        if (categoryFilter && String(t.oddCategory) !== categoryFilter) return false;
        if (periodFilter && t.period !== periodFilter) return false;
        return true;
    }), [tickets, statusFilter, categoryFilter, periodFilter]);

    const counters = useMemo(() => ({
        total: tickets.length,
        available: tickets.filter(t => t.status === 'available').length,
        delivered: tickets.filter(t => t.status === 'delivered').length,
    }), [tickets]);

    const handleLogout = () => {
        signOut(auth);
    };

    const handleDelete = async (ticket) => {
        if (!window.confirm(`Sigur stergi biletul ${ticket.code}?`)) {
            return;
        }
        setWorking(true);
        try {
            await deleteDoc(doc(db, 'tickets', ticket.id));
            setTickets(prev => prev.filter(t => t.id !== ticket.id));
        } catch (err) {
            console.error(err);
            alert('Stergerea a esuat: ' + err.message);
        }
        setWorking(false);
    };

    const handleDeleteAll = async () => {
        if (deleteAllText !== 'STERGE') {
            return;
        }
        if (!window.confirm(`Sigur stergi TOATE cele ${tickets.length} bilete?`)) {
            return;
        }
        setWorking(true);
        try {
            // Firestore accepta maxim 500 operatii per batch
            for (let i = 0; i < tickets.length; i += 500) {
                const batch = writeBatch(db);
                tickets.slice(i, i + 500).forEach(t => batch.delete(doc(db, 'tickets', t.id)));
                await batch.commit();
            }
            setTickets([]);
            setShowDeleteAll(false);
            setDeleteAllText('');
        } catch (err) {
            console.error(err);
            alert('Stergerea a esuat: ' + err.message);
            loadTickets();
        }
        setWorking(false);
    };

    if (!currentUser) {
        return <ApanelLogin/>;
    }

    if (isAdmin === null) {
        return (
            <div className="min-h-screen bg-[#34495e] flex items-center justify-center">
                <p className="text-white">Se verifica accesul...</p>
            </div>
        );
    }

    if (!isAdmin) {
        return <ApanelAccessDenied/>;
    }

    return (
        <div className="min-h-screen bg-[#34495e] p-3 md:p-5">
            <div className="max-w-[1000px] mx-auto space-y-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-white font-bold text-lg">Admin</h1>
                    <div className="flex items-center gap-2">
                        {activeTab === 'tickets' && (
                            <button
                                onClick={loadTickets}
                                disabled={loading || working}
                                className="px-3 py-1.5 bg-[#2ecc71] hover:bg-green-600 text-white text-sm font-bold rounded"
                            >
                                Reincarca
                            </button>
                        )}
                        <button
                            onClick={handleLogout}
                            className="px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-sm font-bold rounded"
                        >
                            Logout
                        </button>
                    </div>
                </div>

                <p className="text-gray-300 text-xs">
                    Admin: {currentUser.email}
                </p>

                <div className="flex gap-2">
                    <button
                        onClick={() => setActiveTab('tickets')}
                        className={
                            'px-4 py-2 rounded-t-lg text-sm font-bold ' +
                            (activeTab === 'tickets' ? 'bg-white text-gray-900' : 'bg-white/20 text-white hover:bg-white/30')
                        }
                    >
                        Bilete
                    </button>
                    <button
                        onClick={() => setActiveTab('images')}
                        className={
                            'px-4 py-2 rounded-t-lg text-sm font-bold ' +
                            (activeTab === 'images' ? 'bg-white text-gray-900' : 'bg-white/20 text-white hover:bg-white/30')
                        }
                    >
                        Imagini
                    </button>
                    <button
                        onClick={() => setActiveTab('reviews')}
                        className={
                            'px-4 py-2 rounded-t-lg text-sm font-bold ' +
                            (activeTab === 'reviews' ? 'bg-white text-gray-900' : 'bg-white/20 text-white hover:bg-white/30')
                        }
                    >
                        Reviews
                    </button>
                    <button
                        onClick={() => setActiveTab('customers')}
                        className={
                            'px-4 py-2 rounded-t-lg text-sm font-bold ' +
                            (activeTab === 'customers' ? 'bg-white text-gray-900' : 'bg-white/20 text-white hover:bg-white/30')
                        }
                    >
                        Clienti
                    </button>
                    <button
                        onClick={() => setActiveTab('tracking')}
                        className={
                            'px-4 py-2 rounded-t-lg text-sm font-bold ' +
                            (activeTab === 'tracking' ? 'bg-white text-gray-900' : 'bg-white/20 text-white hover:bg-white/30')
                        }
                    >
                        Tracking
                    </button>
                    <button
                        onClick={() => setActiveTab('tasks')}
                        className={
                            'px-4 py-2 rounded-t-lg text-sm font-bold ' +
                            (activeTab === 'tasks' ? 'bg-white text-gray-900' : 'bg-white/20 text-white hover:bg-white/30')
                        }
                    >
                        Task-uri
                    </button>
                </div>

                {activeTab === 'tickets' && (
                    <>
                        <SubsSettings/>

                        <CodeGenSettings/>

                        <PremiumTicketsSettings/>

                        <HighOddsAISettings/>

                        <TechIssueBannerSettings/>

                        <OpenWebsiteButtonSettings/>

                        <div className="flex flex-wrap gap-2 text-sm">
                            <span className="bg-white rounded px-3 py-1.5 font-bold">Total: {counters.total}</span>
                            <span className="bg-green-100 text-green-700 rounded px-3 py-1.5 font-bold">Available: {counters.available}</span>
                            <span className="bg-blue-100 text-blue-700 rounded px-3 py-1.5 font-bold">Delivered: {counters.delivered}</span>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="px-2 py-1.5 rounded text-sm"
                            >
                                <option value="">Status: toate</option>
                                <option value="available">available</option>
                                <option value="delivered">delivered</option>
                            </select>
                            <select
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                                className="px-2 py-1.5 rounded text-sm"
                            >
                                <option value="">Categorie: toate</option>
                                {categories.map(c => <option key={c} value={String(c)}>cota {c}</option>)}
                            </select>
                            <select
                                value={periodFilter}
                                onChange={(e) => setPeriodFilter(e.target.value)}
                                className="px-2 py-1.5 rounded text-sm"
                            >
                                <option value="">Perioada: toate</option>
                                {periods.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>

                            <button
                                onClick={() => setShowDeleteAll(prev => !prev)}
                                disabled={tickets.length === 0 || working}
                                className="ml-auto px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white text-sm font-bold rounded"
                            >
                                Sterge toate biletele
                            </button>
                        </div>

                        {showDeleteAll && (
                            <div className="bg-red-50 border border-red-300 rounded-lg p-3 space-y-2">
                                <p className="text-sm text-red-700 font-bold">
                                    Scrie STERGE pentru a confirma stergerea tuturor celor {tickets.length} bilete:
                                </p>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={deleteAllText}
                                        onChange={(e) => setDeleteAllText(e.target.value)}
                                        placeholder="STERGE"
                                        className="px-3 py-1.5 border rounded text-sm"
                                    />
                                    <button
                                        onClick={handleDeleteAll}
                                        disabled={deleteAllText !== 'STERGE' || working}
                                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white text-sm font-bold rounded"
                                    >
                                        {working ? '...' : 'Confirma stergerea'}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="hidden md:grid grid-cols-7 gap-2 px-3 text-gray-300 text-xs font-bold uppercase">
                            <span>Cod</span>
                            <span>Meciuri</span>
                            <span>Cota totala</span>
                            <span>Categorie</span>
                            <span>Perioada</span>
                            <span>Status</span>
                            <span className="text-right">Generat / Actiuni</span>
                        </div>

                        {loading && <p className="text-gray-300 text-center py-8">Se incarca...</p>}
                        {loadError && <p className="text-red-300 text-center py-8">{loadError}</p>}
                        {!loading && !loadError && filteredTickets.length === 0 && (
                            <p className="text-gray-300 text-center py-8">Niciun bilet.</p>
                        )}

                        <div className="space-y-2">
                            {filteredTickets.map(ticket => (
                                <TicketRow
                                    key={ticket.id}
                                    ticket={ticket}
                                    expanded={expandedId === ticket.id}
                                    onToggle={() => setExpandedId(prev => prev === ticket.id ? null : ticket.id)}
                                    onDelete={handleDelete}
                                />
                            ))}
                        </div>
                    </>
                )}

                {activeTab === 'images' && <ImagesTab/>}

                {activeTab === 'reviews' && <ReviewsTab/>}

                {activeTab === 'customers' && <CustomersWithoutSubscriptionTab/>}

                {activeTab === 'tracking' && <TrackingTab/>}

                {activeTab === 'tasks' && <TasksTab/>}
            </div>
        </div>
    );
};

export default ApanelPage;
